import { useEffect, useMemo, useState } from "react";

import { Button } from "@/components/button";

import { ConfidenceScorePanel } from "../components/ConfidenceScorePanel";
import { FileThumbnailStrip } from "../components/FileThumbnailStrip";
import { ReplaceFileModal } from "../components/ReplaceFileModal";
import { useDcc } from "../contexts/DesignConfidenceCheckerProvider";
import { useFilePreviewUrl } from "../hooks/useFilePreviewUrl";
import {
  findNextNeedsAttentionId,
  sortResultsForCarousel,
} from "../utils/sortResultsForCarousel";

const EMPTY_PREVIEW_FILE = new File([], "");

interface ResultsStepProps {
  onBack: () => void;
  onContinue: () => void;
}

export function ResultsStep({ onBack, onContinue }: ResultsStepProps) {
  const { session, shell } = useDcc();
  const results = useMemo(
    () => sortResultsForCarousel(session.displayResults),
    [session.displayResults],
  );
  const isContinueDisabled = shell.isContinueDisabled;
  const isInitialLoading = shell.isResultsInitialLoading;
  const onOverride = session.override;
  const onReplace = session.replaceFile;
  const [selectedAttachmentId, setSelectedAttachmentId] = useState<
    null | string
  >(null);
  const [isReplaceOpen, setIsReplaceOpen] = useState(false);

  useEffect(() => {
    if (results.length === 0) {
      setSelectedAttachmentId(null);

      return;
    }

    const selectedStillExists =
      selectedAttachmentId !== null &&
      results.some((result) => result.id === selectedAttachmentId);

    if (!selectedStillExists) {
      setSelectedAttachmentId(results[0]?.id ?? null);
    }
  }, [results, selectedAttachmentId]);

  const selectedIndex = Math.max(
    0,
    results.findIndex((result) => result.id === selectedAttachmentId),
  );
  const selectedResult = results[selectedIndex] ?? null;
  const selectedPreviewUrl = useFilePreviewUrl(
    selectedResult?.file ?? EMPTY_PREVIEW_FILE,
    selectedResult?.remoteUrl ?? null,
  );
  const isSelectedTransferring = selectedResult?.isTransferring ?? false;
  const showPreview =
    !isInitialLoading &&
    selectedResult !== null &&
    selectedPreviewUrl !== null &&
    !isSelectedTransferring;

  const handleOverrideScore = () => {
    if (!selectedResult) return;
    if (
      selectedResult.tag === "auto_checked" ||
      selectedResult.tag === "overridden"
    )
      return;
    if (selectedResult.evaluationId === null) return;

    const nextId = findNextNeedsAttentionId(results, selectedIndex);

    onOverride(selectedResult.evaluationId);

    if (nextId) {
      setSelectedAttachmentId(nextId);
    }
  };

  const handleReplaceFile = () => {
    if (!selectedResult) return;
    if (selectedResult.isTransferring) return;

    setIsReplaceOpen(true);
  };

  const handleConfirmReplace = (file: File) => {
    if (!selectedResult) return;

    onReplace({
      attachmentId: Number(selectedResult.id),
      file,
    });
  };

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="flex flex-1 overflow-hidden">
        <div className="flex flex-1 flex-col overflow-hidden p-[16px] bg-bg-secondary">
          {results.length > 0 && (
            <FileThumbnailStrip
              onSelectIndex={(index) => {
                const result = results[index];

                if (result) {
                  setSelectedAttachmentId(result.id);
                }
              }}
              results={results}
              selectedIndex={selectedIndex}
            />
          )}
          <div className="relative flex flex-1 items-center justify-center overflow-hidden bg-bg-secondary">
            {isInitialLoading ? (
              <div className="flex flex-col items-center gap-4">
                <div className="h-14 w-14 animate-spin rounded-full border-4 border-border-brand border-t-transparent" />
                <div className="flex flex-col items-center gap-1 text-center">
                  <span className="text-base font-semibold text-text-primary">
                    Running AI confidence checks...
                  </span>
                  <span className="text-sm text-text-tertiary">
                    This will take just a moment
                  </span>
                </div>
              </div>
            ) : isSelectedTransferring && selectedResult ? (
              <div className="flex flex-col items-center gap-4">
                <div className="h-14 w-14 animate-spin rounded-full border-4 border-border-brand border-t-transparent" />
                <div className="flex flex-col items-center gap-1 text-center">
                  <span className="text-base font-semibold text-text-primary">
                    Uploading replacement...
                  </span>
                  <span className="text-sm text-text-tertiary">
                    {selectedResult.uploadProgress === undefined
                      ? "Please wait"
                      : `${selectedResult.uploadProgress}% complete`}
                  </span>
                </div>
              </div>
            ) : (
              showPreview && (
                <div className="flex h-full flex-col items-start gap-2 overflow-y-auto p-4">
                  <span className="text-xs text-text-tertiary">
                    {selectedResult.displayName ?? selectedResult.file.name}
                  </span>
                  <img
                    alt={selectedResult.displayName ?? selectedResult.file.name}
                    className="max-h-full max-w-full rounded-lg object-contain shadow-sm"
                    src={selectedPreviewUrl}
                  />
                </div>
              )
            )}
          </div>
        </div>
        <ConfidenceScorePanel
          onOverrideScore={handleOverrideScore}
          onReplaceFile={handleReplaceFile}
          result={
            isInitialLoading || isSelectedTransferring ? null : selectedResult
          }
        />

        <ReplaceFileModal
          isOpen={isReplaceOpen}
          onOpenChange={setIsReplaceOpen}
          onReplace={handleConfirmReplace}
          originalMime={selectedResult?.file.type ?? "image/png"}
        />
      </div>
      <div className="flex items-center justify-end border-t border-border-secondary px-6 py-4">
        <div className="flex gap-3">
          <Button color="gray" onPress={onBack} size="lg" variant="secondary">
            Back
          </Button>
          <Button
            color="color"
            isDisabled={isContinueDisabled}
            onPress={onContinue}
            size="lg"
            variant="primary"
          >
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
}
